import React, { useEffect, useState } from "react";
import "./index.css";
import { useLocation } from "react-router-dom";
import { CartList } from "../..";
import { Col, Row } from "reactstrap";
import LiveLocationMap from "../../../../../components/LiveLocationMap";
import { Card, Placeholder } from "react-bootstrap";
import { DEFAULT_IMAGE_URL } from "../../../../../constants/constans";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faStarHalfAlt } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from "react-redux";
import { useCartFullData } from "../../../../../hooks/useCartFullData";

const parseCoordinate = (coordStr: string): number => {
  if (!coordStr) return 0;
  const match = coordStr.match(/^([\d.]+)/);
  if (match) {
    const value = parseFloat(match[1]);
    if (coordStr.includes('S') || coordStr.includes('W')) {
      return -value;
    }
    return value;
  }
  return 0;
};

const extractLatLng = (location: any): { lat: number; lng: number } => {
  let lat = 0;
  let lng = 0;
  
  if (!location) return { lat, lng };
  
  if (location.latitude !== undefined && location.longitude !== undefined) {
    lat = location.latitude;
    lng = location.longitude;
  }
  else if (location._latitude !== undefined && location._longitude !== undefined) {
    lat = location._latitude;
    lng = location._longitude;
  }
  else if (location._lat !== undefined && location._long !== undefined) {
    lat = location._lat;
    lng = location._long;
  }
  else if (Array.isArray(location) && location.length >= 2) {
    lat = parseCoordinate(location[0].toString());
    lng = parseCoordinate(location[1].toString());
  }
  else if (location[0] && location[1]) {
    lat = parseCoordinate(location[0].toString());
    lng = parseCoordinate(location[1].toString());
  }
  
  return { lat, lng };
};

const CartControllerOverView = () => {
  const location = useLocation();
  
  // Get cart from Redux store FIRST
  const selectedCartFromRedux = useSelector((state: any) => state.cart.selectedCart);
  
  // Then get from location state (for backward compatibility)
  const locationState = location.state as { carts?: CartList } || {};
  const incomingCarts = locationState.carts;
  
  // Use Redux cart as primary source, fallback to location state
  const carts = selectedCartFromRedux || incomingCarts;
  
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mapError, setMapError] = useState<string | null>(null);
  
  const locationData = carts?.liveLocation || carts?.location;
  const { lat, lng } = extractLatLng(locationData);
  
  const {
    cartPerson,
    loadingCartPerson
  } = useCartFullData({
    cartId: carts?.id || '',
    skip: !carts?.id
  });

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 750);
  }, []);

  // Validate coordinates
  useEffect(() => {
    if (lat && lng && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      setMapError(null);
    } else {
      setMapError('Invalid coordinates');
    }
  }, [lat, lng]);

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

    return (
      <>
        {[...Array(fullStars)].map((_, index) => (
          <FontAwesomeIcon key={index} icon={faStar} className="text-warning" />
        ))}
        {halfStar && <FontAwesomeIcon icon={faStarHalfAlt} className="text-warning" />}
        {[...Array(emptyStars)].map((_, index) => (
          <FontAwesomeIcon key={index} icon={faStar} className="text-muted" />
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <Card className="p-4">
        <Row className="g-4">
          <Col md={4}>
            <Placeholder as={Card.Img} animation="wave" style={{ width: "100%", height: "150px" }} />
          </Col>
          <Col md={8}>
            <Row>
              <Col md={3}>
                <Placeholder xs={6} />
              </Col>
              <Col md={9}>
                <Placeholder xs={8} />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={3}>
                <Placeholder xs={6} />
              </Col>
              <Col md={9}>
                <Placeholder xs={8} />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={3}>
                <Placeholder xs={6} />
              </Col>
              <Col md={9}>
                <Placeholder xs={8} />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={8}></Col>
          <Col md={4}>
            <Placeholder animation="wave" style={{ width: "100%", height: "150px" }} />
          </Col>
        </Row>
      </Card>
    );
  }

  // If no cart data, show message
  if (!carts) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <h5 className="text-muted">
          <i className="fas fa-shopping-cart me-2"></i>
          No cart data available
        </h5>
      </div>
    );
  }

  return (
    <>
      <div className="mt-3"><h3>Cart Details</h3></div>
      <Row className="pt-5">
        <Col lg="4">
          <img 
            src={carts.imageUrl || DEFAULT_IMAGE_URL} 
            width="50%" 
            height="50%" 
            alt={carts.displayName} 
          />
        </Col>
        <Col lg="8">
          <Row>
            <Col lg="4">Cart Name</Col>
            <Col lg="2">:</Col>
            <Col lg="6">{carts.displayName}</Col>
          </Row>
          <Row>
            <Col lg="4">Status</Col>
            <Col lg="2">:</Col>
            <Col lg="6">
              <span className={`badge ${carts.cartState === 'Online' ? 'bg-success' : 'bg-secondary'}`}>
                {carts.cartState === "onTheWay" ? "On The Way" : carts.cartState || 'N/A'}
              </span>
            </Col>
          </Row>
          
          <Row>
            <Col lg="4">Location</Col>
            <Col lg="2">:</Col>
            <Col lg="6">
              {lat && lng && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 ? (
                <LiveLocationMap
                  latitude={lat}
                  longitude={lng}
                />
              ) : (
                <div className="text-muted">
                  <p>Location not available</p>
                  {locationData && (
                    <small className="d-block text-danger">
                      Format received: {JSON.stringify(locationData)}
                    </small>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      
      <hr/>
      
      <div className="mt-3"><h3>Cart Person Details</h3></div>
      {loadingCartPerson ? (
        <Row className="pt-5">
          <Col lg="4">
            <Placeholder animation="wave">
              <Placeholder xs={12} style={{ width: "60%", height: "150px" }} />
            </Placeholder>
          </Col>
          <Col lg="8">
            <Placeholder animation="wave">
              <Placeholder xs={8} /> <Placeholder xs={6} /> <Placeholder xs={10} />
            </Placeholder>
          </Col>
        </Row>
      ) : !cartPerson ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <h5 className="text-muted">
            <i className="fas fa-user-slash me-2"></i>
            {carts.displayName} cart is not assigned to any cart person.
          </h5>
        </div>
      ) : (
        <Row className='pt-4'>
          <Col lg="3" className="text-center">
            <img 
              src={cartPerson.photoUrl || carts.owner?.photoUrl || DEFAULT_IMAGE_URL} 
              alt={cartPerson.displayName} 
              className="rounded-circle img-fluid border"
              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
            />
            <div className="mt-3">
              <h5 className="mb-1">{cartPerson.displayName}</h5>
              <div className="mt-2">
                {renderStars(4.5)}
              </div>
            </div>
          </Col>
          <Col lg="9">
            <Card className="border-0 bg-light">
              <Card.Body>
                <h5 className="mb-4">Contact Information</h5>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <h6 className="text-muted mb-1">Email Address</h6>
                      <p className="h6">{cartPerson.email || 'Not Available'}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <h6 className="text-muted mb-1">Phone Number</h6>
                      <p className="h6">{cartPerson.phoneNumber || 'Not Available'}</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default CartControllerOverView;